import { useEffect } from "react";
import FooterComponent from "../../Components/Footer/FooterComponent";
import styles from "./SalesPitchAutomation.module.css";

const SalesPitchAutomation = () => {
  // Scroll animation effect
  useEffect(() => {
    const sections = document.querySelectorAll(`.${styles.section}, .${styles.ctaSection}, .${styles.hero}`);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.visible);
          }
        });
      },
      { threshold: 0.1 }
    );
    sections.forEach((sec) => observer.observe(sec));
  }, []);

  return (
    <div className={styles.container}>
      {/* Hero Section */}
      <section className={`${styles.hero} ${styles.fadeIn}`}>
        <h1>AI Sales Agent – Automate Your Sales Pitches & Boost Conversions</h1>
        <p>
          ToxBox AI empowers your sales team with AI-driven sales pitch automation.
          Deliver personalized pitches instantly, engage leads, and close deals
          faster without manual follow-ups.
        </p>
        <button className={styles.cta}>Automate Your Sales Pitches Now</button>
      </section>

      {/* Why Choose */}
      <section className={styles.section}>
        <h2>Why Choose ToxBox AI Sales Pitch Automation?</h2>
        <p>
          Our AI Sales Agent transforms sales teams by automating personalized<br/>
          pitches and follow-ups. Reduce repetitive tasks, prevent missed<br/>
          opportunities, and improve lead engagement. With ToxBox AI, every pitch<br/>
          is precise, timely, and conversion-focused.
        </p>
      </section>

      {/* How It Works */}
      <section className={styles.section}>
  <h2>How Our AI Sales Pitch Automation Works</h2>
  <div className={styles.steps}>
    <div className={styles.step}>
      <div className={styles.icon}>📥</div>
      <h3>Import Leads</h3>
      <p>Import your leads or sync directly with your CRM.</p>
    </div>
    <div className={styles.step}>
      <div className={styles.icon}>🧠</div>
      <h3>AI Analysis</h3>
      <p>AI analyzes lead profiles and engagement patterns.</p>
    </div>
    <div className={styles.step}>
      <div className={styles.icon}>⚡</div>
      <h3>Generate Pitches</h3>
      <p>Automatically generate personalized, high-converting sales pitches.</p>
    </div>
    <div className={styles.step}>
      <div className={styles.icon}>📩</div>
      <h3>Multi-Channel Delivery</h3>
      <p>Deliver pitches instantly via email, chat, or calls.</p>
    </div>
    <div className={styles.step}>
      <div className={styles.icon}>📊</div>
      <h3>Track & Follow-Up</h3>
      <p>Track responses and schedule AI-driven follow-ups seamlessly.</p>
    </div>
  </div>
</section>


      {/* Features */}
      <section className={styles.section}>
        <h2>Powerful Features of ToxBox AI Sales Agent</h2>
        <div className={styles.cards}>
          <div className={styles.card}>✨ Personalized AI-Generated Sales Pitches</div>
          <div className={styles.card}>💬 Multi-Channel Automation (Email, Chat, Call)</div>
          <div className={styles.card}>🔗 Real-Time CRM Integration (Salesforce, HubSpot, Zoho)</div>
          <div className={styles.card}>📈 Engagement Tracking & Analytics</div>
          <div className={styles.card}>⏳ Automated Follow-Up Sequences</div>
        </div>
      </section>

      {/* Engage Leads */}
      <section className={styles.ctaSection}>
  <div className={styles.ctaContent}>
    <h2>⚡ Engage Leads Instantly with AI</h2>
    <p>
      Deliver personalized sales pitches instantly. <strong>ToxBox AI</strong> handles pitch
      generation, delivery, and follow-ups, ensuring that every lead receives
      the most compelling message for <span className={styles.highlight}>higher conversion rates</span>.
    </p>
    <button className={styles.cta}>🚀 Start Automating Now</button>
  </div>
</section>


      {/* Why Businesses Choose */}
      <section className={styles.whySection}>
  <div className={styles.overlay}></div>
  <h2>
    Why Businesses & Sales Teams <span>Choose ToxBox AI</span>
  </h2>
  <ul className={styles.list}>
    <li>Save time with automated pitch generation</li>
    <li>Increase conversion rates with personalized messaging</li>
    <li>Reduce manual follow-ups and missed opportunities</li>
    <li>Improve sales team productivity</li>
    <li>Gain actionable insights with AI analytics</li>
  </ul>
</section>


      {/* CRM Integration */}
      <section className={styles.crmSection}>
  <div className={styles.crmContent}>
    <h2>Seamless CRM Integration for Effortless Sales Automation</h2>
    <p>
      ToxBox AI integrates with <strong>Salesforce, HubSpot, Zoho</strong>, and more.  
      Sync leads, track engagement, and automate follow-ups in real-time, creating a  
      seamless, data-driven sales workflow.
    </p>
    <button className={styles.cta}>🚀 Integrate Your CRM Today</button>
  </div>
</section>

{/* Trusted by Teams */}
<section className={styles.trustedSection}>
  <div className={styles.trustedContent}>
    <h2>Trusted by Sales Teams Worldwide 🌍</h2>
    <p>
      See how <strong>ToxBox AI Sales Agent</strong> has transformed sales processes, improved  
      lead engagement, and boosted conversions for businesses of all sizes.  
      Join hundreds of satisfied sales teams using AI to close more deals efficiently.
    </p>
  </div>
</section>


      {/* FAQ */}
      <section className={styles.section}>
        <h2>Frequently Asked Questions</h2>
        <div className={styles.faq}>
          <div>
            <strong>Can AI personalize pitches for each lead?</strong>
            <p>Yes, dynamically based on lead profile and behavior.</p>
          </div>
          <div>
            <strong>Is my data secure?</strong>
            <p>Absolutely, we use encrypted storage and follow industry standards.</p>
          </div>
          <div>
            <strong>Can it automate follow-ups?</strong>
            <p>Yes, AI sequences follow-ups based on lead engagement.</p>
          </div>
          <div>
            <strong>Does it integrate with my CRM?</strong>
            <p>Yes, seamless integration with Salesforce, HubSpot, Zoho, and more.</p>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className={styles.pricingSection}>
  <div className={styles.card}>
    <h2>💼 Flexible Pricing for Every Sales Team</h2>
    <p>
      Choose a plan that fits your team’s needs. Start free or upgrade for
      premium features such as unlimited AI-generated pitches, advanced
      analytics, multi-channel automation, and priority support. Scale your
      sales effortlessly with ToxBox AI.
    </p>
    <button className={styles.cta}>View Pricing Plans</button>
  </div>
</section>

{/* Final CTA */}
<section className={styles.ctaSection}>
  <div className={styles.cardGlow}>
    <h2>🚀 Automate Your Sales Pitches Today</h2>
    <p>
      Stop wasting time on repetitive messaging. Let <strong>ToxBox AI</strong> craft,
      deliver, and follow up on pitches automatically, so your sales team can
      focus on closing deals. Start smarter, faster sales automation today.
    </p>
    <button className={styles.cta}>Automate Now</button>
  </div>
</section>

    </div>
  );
};

export default SalesPitchAutomation;
